import { useEffect } from 'react';
import '../styles/App.css';
import ExpertPortalContainer from '../containers/ExpertPortalContainer';
import { Router, Route as BaseRoute, Switch, Redirect } from 'react-router-dom';
import styled from 'styled-components/macro';
import Header from './Header/Header';
import LoginPage from './LoginPage/LoginPage';
import MFAPage from './MFAPage/MFAPage';
import SignUpExpertComponent from './SignUp/SignUpExpert';
import PasswordResetComponent from './ResetPassword/ResetPassword';
import ConfirmPopup from './popups/ConfirmPopup';
import FourOhFourPage from './404Page/404Page';
import { syncStoreWithCurrentUserState } from '../util/loaderUtils';
import BlockingSpinner from './BlockingSpinner/BlockingSpinner';
import { showError } from '../util/errorPopupUtil';
import ExpertDashboard from './ExpertDashboard/ExpertDashboard';
import 'rc-tooltip/assets/bootstrap_white.css';
import { getBlockingSpinnerActiveStatus } from 'extractFromState';
import { setChatAlertActiveId, updateUser, changeLoginStatus } from 'actions';
import { persistor } from 'store';
import { handleError } from 'services/ErrorService/ErrorService';
import { useDispatch, useSelector } from 'react-redux';
import { getSessionToken } from 'util/parseSelectors';

import PreLoginPage from './SSO/PreLoginPage/PreLoginPage';
import SSOLoginPage from './SSO/SSOLoginPage/SSOLoginPage';
import SSOCallbackPage from './SSO/SSOCallbackPage/SSOCallbackPage';
import SignUpOptionsPage from './SSO/SignUpOptionsPage/SignUpOptionsPage';
import AuthService from 'services/authService';
import * as SimplrErrors from '@asurion-core/typescript-utils/SimplrErrors';
import { SignUpGeneric } from './SignUp/SignUpGeneric';
import environment from '../environment';
import { MonitoringRoute, clearData } from '@asurion-core/frontend-utils/lib/WebObservability';
import { browserHistory } from '../browserHistory';

const App = () => {
  const dispatch = useDispatch();
  const loggedIn = useSelector((state) => state.user.loggedIn);
  const blockingSpinnerActive = useSelector((state) => getBlockingSpinnerActiveStatus(state));
  const Route = environment.observability.isTelemetryEnabled ? MonitoringRoute : BaseRoute;

  useEffect(() => {
    const initializeComponent = async () => {
      try {
        await syncStoreWithCurrentUserState((user) => dispatch(updateUser(user)));
      } catch (err) {
        showError(err);
      }
      try {
        if (!getSessionToken()) {
          throw new SimplrErrors.SimplrError({
            code: SimplrErrors.Codes.AUTHENTICATION_ERROR,
            message: 'Invalid session',
          });
        }
      } catch (e) {
        try {
          if (environment.observability.isTelemetryEnabled) {
            clearData();
          }

          await persistor.purge();
          await AuthService.logout();
        } catch (error) {
          handleError({ error, customMessage: 'Error while calling parse logout', showPopup: true });
        }
        dispatch(changeLoginStatus(false));
      }
    };
    initializeComponent();
  }, [dispatch]);

  const content = (
    <>
      <Route path="/home/expert-dashboard" component={ExpertDashboard} />
      <Switch>
        <Route exact path="/pre-login" render={() => <PreLoginPage />} />
        <Route exact path="/sign-up-options" render={() => <SignUpOptionsPage />} />
        <Route exact path="/sso-login" render={() => <SSOLoginPage />} />
        <Route exact path="/sso" render={() => <SSOCallbackPage redirectDelay={1000} />} />
        <Route exact path="/login" render={() => (loggedIn ? <Redirect to="/home" /> : <LoginPage />)} />
        <Route exact path="/mfa" render={() => (loggedIn ? <Redirect to="/home" /> : <MFAPage />)} />
        <PrivateRoute path="/home" component={ExpertPortalContainer} loggedIn={loggedIn} />
        <Route exact path="/signup/expert" component={SignUpExpertComponent} />
        <Route exact path="/signup" component={SignUpGeneric} />
        <Route
          exact
          path="/password/reset"
          render={({ location }) => <Redirect to={`/password/reset/expert${location && location.search}`} />}
        />
        <Route exact path="/password/reset/:accountType" component={PasswordResetComponent} />
        <Route component={FourOhFourPage} />
      </Switch>
    </>
  );

  return (
    <Router history={browserHistory}>
      <div>
        <ConfirmPopup />
        {blockingSpinnerActive && <BlockingSpinner />}
        <Route path="/home" component={Header} />
        <Route exact path="/" render={() => (loggedIn ? <Redirect to="/home" /> : <PreLoginPage />)} />
        <BackgroundHolder onClick={() => dispatch(setChatAlertActiveId(null))}>
          <BackgroundHolderInner>{content}</BackgroundHolderInner>
        </BackgroundHolder>
      </div>
    </Router>
  );
};

const PrivateRoute = ({ component: Component, loggedIn, ...rest }) => {
  const Route = environment.observability.isTelemetryEnabled ? MonitoringRoute : BaseRoute;

  return (
    <Route
      {...rest}
      render={(props) => {
        return !loggedIn ? (
          <Redirect
            to={{
              pathname: '/',
            }}
          />
        ) : (
          <Component {...props} />
        );
      }}
    />
  );
};

export default App;

const BackgroundHolder = styled.div`
  flex: 1 1;
  max-height: 100%;
  overflow-y: auto;
`;

const BackgroundHolderInner = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;
